'use client';
import React, { useState, useEffect, useMemo } from 'react';
import { ICommit } from '@/types';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import Button from '@/ui/Button';
import * as Icon from '@/ui/Icon';

import { colors } from '@/styles';
import { IRepository, IUser } from '@/types';
import { Row, Stack, Card } from '@/ui/primatives';
import Input from '@/components/Input';
import Tag from '@/ui/Tag';

import { IResponseError } from '@/lib/apiBase';

import DownloadProgressIndicator from '@/components/DownloadProgressIndicator';
import { TDownloadProgress } from '@/lib/repositoryFileApi';
import tracking from '@/lib/tracking';
import { createMarimoFileTemplate } from '@/lib/marimoSandboxesApi';
// import { DevTool } from '@hookform/devtools';

type FormInputs = {
  filepath?: string;
  commitMessage: string;
};

const formSchema = Yup.object().shape({
  commitMessage: Yup.string().required('Required'),
  filepath: Yup.string().matches(/^[a-zA-Z0-9/_-]+\.py$/, 'Must be a valid python file path ending in .py'),
});

export type TNewFileFormCompleteData = {
  repository: IRepository;
  branch: string;
  targetPath: string;
  commit: ICommit;
};

type TNewFileFormProps = {
  user: IUser;
  repository: IRepository;
  branch?: string;
  resourcePath?: string;
  testProgress?: TDownloadProgress;
  onComplete: (data: TNewFileFormCompleteData) => void;
};

const NewFileForm: React.FC<TNewFileFormProps> = ({
  repository,
  branch = repository.default_branch || 'main',
  resourcePath,
  testProgress = null,
  onComplete,
}) => {
  const [systemError, setSystemError] = useState<IResponseError>();
  const [targetPath, setTargetPath] = useState<string | undefined>(resourcePath);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [progress, setProgress] = useState<TDownloadProgress | null>(testProgress);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(formSchema),
    reValidateMode: 'onChange',
  });

  const watchPath = watch('filepath');

  useEffect(() => {
    setTargetPath([resourcePath, watchPath].filter((val) => val).join('/'));
    if (watchPath) {
      setValue("commitMessage", `Add ${watchPath} notebook`);
    } else {
      setValue("commitMessage", "");
    }
  }, [resourcePath, watchPath]);

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    const targetPath = [resourcePath, data.filepath].filter((val) => val).join('/');

    setSubmitting(true);
    setStatusMessage('Creating file...');

    const { status, error, commit } = await createMarimoFileTemplate({
      repository: repository,
      branch: branch,
      commitMessage: data.commitMessage,
      resource: targetPath,
    });

    if (status === 'success') {
      tracking.capture('notebook_file_create');
      setStatusMessage('Complete. Redirecting...');
      onComplete({ repository, branch, targetPath, commit: commit as ICommit });
    } else if (status == 'error' && error) {
      console.log('error', error);
      setStatusMessage('Error creating notebook. Please try again.');
      setSystemError(error);
      setSubmitting(false);
    } else {
      setSubmitting(false);
      setStatusMessage(null);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={20}>
          <Input
            prefixElement={<Tag variant="info">{resourcePath}/</Tag>}
            label="File Path"
            name="filepath"
            placeholder="my-notebook.py"
            submit={register}
            error={errors.filepath}
            hint="The notebook will be created in the specified directory"
          />

          <Input
            style={{ flex: 1 }}
            wide
            label="Commit Message"
            name="commitMessage"
            submit={register}
            error={errors.commitMessage}
          />

          {systemError && (
            <Row gap={10} style={{ color: colors.semantic.negative.dark }}>
              <Icon.XCircle />
              <span>{systemError.detail}</span>
            </Row>
          )}
          <Row gap={20}>
            <Button.Black small disabled={submitting}>
              {submitting ? 'Creating Notebook...' : 'Create Notebook file'}
            </Button.Black>
          </Row>
          {statusMessage && <div>{statusMessage}</div>}
          {progress && <DownloadProgressIndicator progress={progress} />}
        </Stack>
      </form>
      {/* <DevTool control={control} /> */}
    </div>
  );
};

export default NewFileForm;
